import { cx } from '@linaria/core';
import { useRef, useState, useEffect, useMemo, useCallback, memo } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { findNearestScroller, getAbsoluteNodeOffset } from '@utils/common';
import { useVirtualizedRows, ROW_CHUNK_SIZE, SizeTransition } from '@utils/react';
import { ROW_HEIGHT } from './row.style.js';
import { headerGroup, table, transitionWrapper, tableBody, bodyCrossfadeDurationMs, selectors, emptyMessage, transitionableRow, rowTransitionDurationMs } from './table.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';

/**
 * Renders a table which brings an overview over all courses available to a
 * given user.
 *
 * Do note that sematic HTML elements have not been used to allow for proper
 * transitions when filtering content.
 * Instead, content has been made accesible using ARIA roles.
 * See https://stackoverflow.com/questions/49946960/accessible-div-table
 */

function Table(props) {
  const tableRef = useRef(null);
  const [scroller, setScroller] = useState(props.scroller ?? findNearestScroller(tableRef));
  const [offsetTop, setOffsetTop] = useState();
  useEffect(() => {
    if (!scroller) {
      setScroller(findNearestScroller(tableRef));
    }
    if (typeof offsetTop === "undefined") {
      setOffsetTop(getAbsoluteNodeOffset(tableRef.current).y ?? 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/jsxs("div", {
    ref: tableRef,
    className: cx(table, props.className),
    role: "grid",
    children: [/*#__PURE__*/jsx("div", {
      role: "rowgroup",
      className: headerGroup,
      children: props.thead
    }), /*#__PURE__*/jsx(Content, {
      ...props,
      tableRef: tableRef,
      scroller: scroller,
      offsetTop: offsetTop ?? 0
    })]
  });
}
function Content(props) {
  const getRowHeightEstimate = useMemo(() => props.getRowHeightEstimate ?? (() => ROW_HEIGHT.default), [props.getRowHeightEstimate]);
  const virtualised = useVirtualizedRows({
    cacheKey: undefined,
    contentKey: props.contentKey,
    rows: props.items,
    scroller: props.scroller,
    offsetTop: props.offsetTop,
    tableRef: props.tableRef,
    extraChunksToRender: (props.maxChunksRendered ?? 6) / 2,
    getRowHeightEstimate: getRowHeightEstimate,
    getBoundaryHeight: props.getMaxHeight,
    columnCount: 1,
    initialChunks: props.initialChunks ?? 2,
    maxChunksRendered: props.maxChunksRendered ?? 6,
    chunkSize: props.chunkSize ?? ROW_CHUNK_SIZE
  });
  const getMinSizeInPx = useCallback(() => ({
    height: props.getMinHeight?.()
  }), [props]);
  const getMaxSizeInPx = useCallback(() => ({
    height: props.getMaxHeight?.()
  }), [props]);
  return /*#__PURE__*/jsx(TransitionGroup, {
    className: transitionWrapper,
    children: /*#__PURE__*/jsx(SizeTransition, {
      axis: "vertical",
      swappable: true
      /**
       * Cap minHeight and maxHeight such that we at most allow the height
       * of the table to transition from the min-height of the user-area to
       * the max-height of the userarea
       */,
      getMinSizeInPx: getMinSizeInPx,
      getMaxSizeInPx: getMaxSizeInPx,
      timeout: bodyCrossfadeDurationMs,
      classNames: tableBody,
      children: ref => /*#__PURE__*/jsx("div", {
        ref: ref,
        role: "rowgroup",
        className: tableBody,
        children: virtualised.chunks.length > 0 ? virtualised.chunks.map((chunk, index) => {
          const isActive = virtualised.activeChunks[index] ?? false;
          const paddingBottom = (() => {
            if (isActive) {
              return undefined;
            }
            const chunkHeight = virtualised.getChunkHeight(index);
            if (chunkHeight) {
              return chunkHeight;
            }
            let chunkHeightThroughItems = 0;
            for (let itemIndex = 0; itemIndex < (virtualised.chunks[index]?.length ?? 0); itemIndex++) {
              const item = virtualised.chunks[index]?.[itemIndex];
              chunkHeightThroughItems += virtualised.getRowHeight(item, index * (props.chunkSize ?? ROW_CHUNK_SIZE) + itemIndex);
            }
            return chunkHeightThroughItems;
          })();
          return /*#__PURE__*/jsx("div", {
            ref: virtualised.determineChunkHeight,
            className: selectors.chunk.className,
            "data-index": index,
            "data-active": isActive,
            style: {
              paddingBottom
            },
            children: /*#__PURE__*/jsx(Chunk, {
              contentKey: chunk.map(it => "id" in it ? it.id : it.key).join(","),
              chunk: chunk,
              chunkIndex: index,
              activeChunks: virtualised.activeChunks,
              determineRowHeight: virtualised.determineHeight,
              additionalProps: props.additionalProps,
              chunkSize: props.chunkSize ?? ROW_CHUNK_SIZE,
              isActive: isActive,
              Item: props.Item
            })
          }, index);
        }) : props.emptyMessage && /*#__PURE__*/jsx("div", {
          className: emptyMessage,
          children: props.emptyMessage
        })
      })
    }, props.contentKey)
  });
}
const Chunk = /*#__PURE__*/memo(function Chunk(props) {
  if (!props.isActive) {
    return null;
  }
  return /*#__PURE__*/jsx(TransitionGroup, {
    component: null,
    children: props.chunk.map((item, index) => /*#__PURE__*/jsx(SizeTransition, {
      axis: "vertical",
      timeout: rowTransitionDurationMs,
      classNames: transitionableRow,
      mountOnEnter: true,
      unmountOnExit: true,
      children: ref => /*#__PURE__*/jsx("div", {
        ref: ref,
        className: transitionableRow,
        children: /*#__PURE__*/jsx("div", {
          ref: props.determineRowHeight,
          "data-index": index + props.chunkIndex * props.chunkSize,
          children: /*#__PURE__*/jsx(props.Item, {
            ...props.additionalProps,
            item: item,
            index: index + props.chunkIndex * props.chunkSize
          })
        })
      }, index)
    }, item.key))
  });
});

export { Table, selectors as tableSelectors };
//# sourceMappingURL=table.js.map
